import React, { useState, useEffect, useCallback, useMemo, memo } from "react"
import { useSelector, useDispatch } from "react-redux"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import Button from "@material-ui/core/Button"
import { makeStyles } from "@material-ui/core/styles"
import ControlPanelTextField from "@/components/common/ControlPanelTextField"
import validateAddParticipantForm from "@/helpers/validateAddParticipantForm"
import ProgressBar from "@/components/common/ProgressBar"
import { controlPanelSidebarOpenComponent } from "@/redux/actions/controlPanelSidebarAction"
import { deselectedParticipant } from "@/redux/actions/controlPanelSelectedParticipantAction"
import useDebounce from "@/hooks/useDebounce"
import ModalDelete from "@/components/control-panel/control-panel-modal/ModalDelete"
import ModalReminderFinishAssessment from "@/components/control-panel/control-panel-modal/ModalReminderFinishAssessment"
import useHandleModalShow from "@/hooks/useHandleModalShow"
import { updateParticipantRequest } from "@/redux/actions/participantAction"

const useStyles = makeStyles((theme) => ({
    formWrapper: {
        padding: theme.spacing(6, 4),
    },
    controlPanelSidebarFormIcon: {
        marginRight: theme.spacing(2),
    },
    controlPanelSidebarFormTitle: {
        flex: "0 0 auto",
        color: "#ffffff",
    },
    remindButton: {
        borderColor: "#ffffff",
        color: "#ffffff",
    },
    deleteButton: {
        borderColor: "#ffffff",
        color: "#ffffff",
    },
    participantFormProgressBar: {
        color: "#ffffff",
    },
    close: {
        color: "#ffffff",
        cursor: "pointer",
    },
}))

const initParticipantUpdateFormStatus = {
    participantFirstName: {
        status: false,
        errorMessage: "",
    },
    participantLastName: { status: false, errorMessage: "" },
    participantEmail: { status: false, errorMessage: "" },
    overAll: false,
}

const UpdateForm = memo(() => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const { selectedStatus, selectedParticipantId, selectedParticipantDetail } = useSelector(
        (state) => state.controlPanelSelectedParticipantReducer
    )
    const progress = useMemo(() => selectedParticipantDetail.responseCount, [selectedParticipantDetail])
    const outOf = useMemo(() => selectedParticipantDetail.questionCount, [selectedParticipantDetail])
    const progressLabel = useMemo(() => {
        return `${selectedParticipantDetail.responseCount || 0}/${selectedParticipantDetail.questionCount || 0} questions completed`
    }, [selectedParticipantDetail])

    const [disableUpdateSave, setDisableUpdateSave] = useState(true)
    const [participantUpdateDetail, setParticipantUpdateDetail] = useState({
        participantFirstName: selectedParticipantDetail.firstName,
        participantLastName: selectedParticipantDetail.lastName,
        participantEmail: selectedParticipantDetail.email,
    })
    const [isParticipantUpdateFormInvalid, setIsParticipantUpdateFormInvalid] = useState(initParticipantUpdateFormStatus)

    useEffect(() => {
        setParticipantUpdateDetail({
            participantFirstName: selectedParticipantDetail.firstName,
            participantLastName: selectedParticipantDetail.lastName,
            participantEmail: selectedParticipantDetail.email,
        })
    }, [selectedStatus, selectedParticipantId, selectedParticipantDetail])

    const handleSaveEditParticipant = useDebounce(
        () => {
            const validateParticipantFirstName = validate({
                value: participantUpdateDetail.participantFirstName,
                key: "participantFirstName",
            })
            const validateParticipantLastName = validate({
                value: participantUpdateDetail.participantLastName,
                key: "participantLastName",
            })
            const validateParticipantEmail = validate({
                value: participantUpdateDetail.participantEmail,
                key: "participantEmail",
                extraCheck: "email",
            })
            const validateStatus = validateParticipantFirstName && validateParticipantLastName && validateParticipantEmail
            if (validateStatus) {
                if (participantUpdateDetail) {
                    const participantToUpdate = {
                        participantId: selectedParticipantId,
                        firstName: participantUpdateDetail.participantFirstName,
                        lastName: participantUpdateDetail.participantLastName,
                        email: participantUpdateDetail.participantEmail,
                        projectId: selectedParticipantDetail.projectId,
                    }
                    dispatch(updateParticipantRequest(participantToUpdate))
                }
            }
        },
        500,
        [isParticipantUpdateFormInvalid, participantUpdateDetail]
    )

    const validate = useCallback(({ value, key, extraCheck }) => {
        const validateResult = validateAddParticipantForm({ value, key, extraCheck }, setIsParticipantUpdateFormInvalid)
        return validateResult
    }, [])

    const handleFormChange = useCallback(
        (event) => {
            setIsParticipantUpdateFormInvalid(initParticipantUpdateFormStatus)
            setParticipantUpdateDetail((prev) => ({
                ...prev,
                [event.target.name]: event.target.value,
            }))
            setDisableUpdateSave(false)
        },
        [isParticipantUpdateFormInvalid, participantUpdateDetail]
    )

    const handleEditClose = () => {
        dispatch(deselectedParticipant())
        dispatch(controlPanelSidebarOpenComponent(process.env.SPARCA_CONTROL_PANEL_SIDEBAR_ICONS_SELECT))
    }

    const handleDeleteParticipant = () => {
        handleModalDeleteShowChange(true)
    }

    const {
        modalItemShow: modalDeleteShow,
        handleClose: handleDeleteClose,
        handleModalItemShowChange: handleModalDeleteShowChange,
    } = useHandleModalShow()

    const handleRemindParticipantFinishAssessment = () => {
        handleModalRemindShowChange(true)
    }

    const {
        modalItemShow: modalRemindShow,
        handleClose: handleRemindClose,
        handleModalItemShowChange: handleModalRemindShowChange,
    } = useHandleModalShow()

    return (
        <Box className={classes.formWrapper}>
            <Box display="flex" alignItems="center">
                <Typography variant="h6" component="h2" className={classes.controlPanelSidebarFormTitle}>
                    Edit Participant{" "}
                    <Typography component="span" variant="h6" className={classes.close} onClick={handleEditClose}>
                        ✕
                    </Typography>
                </Typography>
            </Box>
            <Box mt={3} className={classes.participantFormProgressBar}>
                <ProgressBar progress={progress} outOf={outOf} label={progressLabel} />
            </Box>
            <Box className="project-detail-bottom-wrapper" mt={4}>
                <Box mb={3}>
                    <Box>
                        <Box my={2}>
                            <ControlPanelTextField
                                required
                                label="First Name"
                                error={isParticipantUpdateFormInvalid.participantFirstName.status}
                                helperText={isParticipantUpdateFormInvalid.participantFirstName.status && "First name required"}
                                id="participant-first-name"
                                name="participantFirstName"
                                placeholder="Enter First Name"
                                variant="outlined"
                                onChange={handleFormChange}
                                value={participantUpdateDetail.participantFirstName}
                            />
                        </Box>
                        <Box my={2}>
                            <ControlPanelTextField
                                required
                                label="Last Name"
                                error={isParticipantUpdateFormInvalid.participantLastName.status}
                                helperText={isParticipantUpdateFormInvalid.participantLastName.status && "Last name required"}
                                id="participant-last-name"
                                name="participantLastName"
                                placeholder="Enter Last Name"
                                variant="outlined"
                                onChange={handleFormChange}
                                value={participantUpdateDetail.participantLastName}
                            />
                        </Box>
                        <Box my={2}>
                            <ControlPanelTextField
                                required
                                label="Email"
                                error={isParticipantUpdateFormInvalid.participantEmail.status}
                                helperText={
                                    isParticipantUpdateFormInvalid.participantEmail.status &&
                                    (isParticipantUpdateFormInvalid.participantEmail.errorMessage
                                        ? isParticipantUpdateFormInvalid.participantEmail.errorMessage
                                        : "Email required")
                                }
                                id="participant-email"
                                name="participantEmail"
                                placeholder="Enter Email"
                                variant="outlined"
                                onChange={handleFormChange}
                                value={participantUpdateDetail.participantEmail}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box>
                    <Box my={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            aria-label="add"
                            disabled={disableUpdateSave}
                            fullWidth
                            onClick={handleSaveEditParticipant}
                        >
                            Save Edit
                        </Button>
                    </Box>
                    <Box mt={3}>
                        {progress < outOf && (
                            <Box>
                                <Button
                                    className={classes.remindButton}
                                    variant="outlined"
                                    color="primary"
                                    aria-label="add"
                                    fullWidth
                                    onClick={handleRemindParticipantFinishAssessment}
                                >
                                    REMIND
                                </Button>
                            </Box>
                        )}
                        <Box mt={1}>
                            <Button
                                className={classes.deleteButton}
                                variant="outlined"
                                color="primary"
                                aria-label="add"
                                fullWidth
                                onClick={handleDeleteParticipant}
                            >
                                DELETE
                            </Button>
                        </Box>
                    </Box>
                    {/* {newParticipantManuallyAddSuccess ? (
                            <Box color="success.main" mt={3}>
                                <Typography component="div" display="inline" ml={2}>
                                    <AlertWithTimer severity="success">That Participant has been added.</AlertWithTimer>
                                </Typography>
                            </Box>
                        ) : null} */}
                </Box>
            </Box>
            <ModalDelete
                modalDeleteShow={modalDeleteShow}
                handleClose={handleDeleteClose}
                selectedParticipantDetail={selectedParticipantDetail}
            />
            <ModalReminderFinishAssessment
                modalRemindShow={modalRemindShow}
                handleClose={handleRemindClose}
                selectedParticipantDetail={selectedParticipantDetail}
            />
        </Box>
    )
})

export default UpdateForm
